import { useState, useCallback, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { Loader2, ChevronLeft, Pencil } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { toast } from '@/components/ui/use-toast';
import { ConfirmationModal } from '@/components/ui/confirmation-modal';
import { AvailabilityProgressPanel } from './AvailabilityProgressPanel';
import { ProposalGrid } from './ProposalGrid';
import { markProposalConflicts } from './proposalConflicts';
import { RoundScheduleEditor } from './RoundScheduleEditor';
import {
  openRound,
  proposeRound,
  publishRound,
  rescheduleMatch,
  getRound,
} from '@/api/tournament/tournamentRoundApi';
import type { TournamentRound, BackendTeam } from '@/types/tournament-round.model';
import { handleGlobalError } from '@/utils/error-handler';
import type { Club } from '@/types/tournament.model';
import { addDays } from '@/utils/localDateOnly';

interface RoundDetailProps {
  tournamentId: string;
  round: TournamentRound;
  /** Main tournament club (required by the schedule editor). */
  club?: Club | null;
  matchDuration?: number;
  sportUid?: string | null;
  onBack: () => void;
  onRoundUpdated: (updatedRound: TournamentRound) => void;
  /** Called after open/propose/publish to refresh the rest of the view (fixture, groups). */
  onAfterAction?: () => void | Promise<void>;
}

/** Round status label. */
function getRoundStatusLabel(status: TournamentRound['status']): string {
  switch (status) {
    case 'CREATED': return 'Por abrir';
    case 'AVAILABILITY_OPEN': return 'Cargando disponibilidad';
    case 'PROPOSED': return 'Propuesta lista';
    case 'PUBLISHED': return 'Publicada';
    case 'COMPLETED': return 'Cerrada';
    default: return status;
  }
}

/** Derives a team name from its players' names. */
function getTeamName(team: BackendTeam | null | undefined, fallback: string): string {
  if (!team?.players?.length) return fallback;
  const names = team.players
    .map((p) => p.name)
    .filter((n): n is string => Boolean(n));
  return names.length > 0 ? names.join(' / ') : fallback;
}

/**
 * Round detail: status, actions, schedule editor, availability
 * progress, match proposal and rescheduling.
 */
export function RoundDetail({
  tournamentId,
  round,
  club,
  matchDuration = 60,
  sportUid,
  onBack,
  onRoundUpdated,
  onAfterAction,
}: RoundDetailProps) {
  const [isActioning, setIsActioning] = useState(false);
  const [showScheduleEditor, setShowScheduleEditor] = useState(false);
  const [confirmProposeOpen, setConfirmProposeOpen] = useState(false);

  const refreshRound = useCallback(async () => {
    try {
      const updated = await getRound(tournamentId, round.id);
      onRoundUpdated(updated);
    } catch (err) {
      handleGlobalError(err, 'Error', 'No se pudo recargar la jornada');
    }
  }, [tournamentId, round.id, onRoundUpdated]);

  // The list endpoint (listRounds) returns schedule=null for performance; on opening the detail
  // we request the full round to show the seeded/submitted schedule. Depends on whether the
  // schedule is missing, not on the array itself: every response brings a new array reference.
  const scheduleMissing = round.schedule == null;
  useEffect(() => {
    if (scheduleMissing) {
      refreshRound();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [round.id, scheduleMissing]);

  const handleAction = useCallback(
    async (
      action: () => Promise<TournamentRound>,
      /** Extra detail for the success toast, read from the round the action returned. */
      describe?: (updated: TournamentRound) => string | undefined,
    ) => {
      setIsActioning(true);
      try {
        const updated = await action();
        // Apply the detailed round right away. The refresh below re-fetches the round list, and
        // RoundsCard merges it without dropping this schedule, so a slow refresh no longer shows
        // a degraded round in between.
        onRoundUpdated(updated);
        toast({ title: 'Jornada actualizada', description: describe?.(updated) });
      } catch (err) {
        handleGlobalError(err, 'Error', 'No se pudo actualizar la jornada');
        return;
      } finally {
        setIsActioning(false);
      }
      // The action already succeeded and is persisted: a failed refresh must not report it as failed.
      try {
        await onAfterAction?.();
      } catch (refreshErr) {
        handleGlobalError(refreshErr, 'Error', 'No se pudo refrescar el torneo');
      }
    },
    [onRoundUpdated, onAfterAction],
  );

  // The list endpoint carries no siblingRounds, so before the actions that depend on the sibling
  // rounds' state (propose, edit schedule) we reload the detail to get them current.
  const refreshThen = async (next: () => void) => {
    setIsActioning(true);
    try {
      await refreshRound();
    } finally {
      setIsActioning(false);
    }
    next();
  };

  // Opening a round also opens the sibling rounds of the same week that were ready (backend#409): say which.
  const handleOpen = () => {
    const openBefore = new Set(
      (round.siblingRounds ?? []).filter((s) => s.status === 'AVAILABILITY_OPEN').map((s) => s.roundId),
    );
    return handleAction(
      () => openRound(tournamentId, round.id),
      (updated) => {
        const openedTogether = (updated.siblingRounds ?? []).filter(
          (s) => s.status === 'AVAILABILITY_OPEN' && !openBefore.has(s.roundId),
        );
        if (openedTogether.length === 0) return undefined;
        return `Se abrió también: ${openedTogether
          .map((s) => `fecha ${s.roundNumber} de ${s.tournamentName}`)
          .join(', ')}.`;
      },
    );
  };

  const handlePropose = () =>
    handleAction(() => proposeRound(tournamentId, round.id));

  const handlePublish = () =>
    handleAction(() => publishRound(tournamentId, round.id));

  /** Returns whether the match was rescheduled, so the row keeps its panel open on failure. */
  const handleReschedule = async (matchId: string, startTime: string): Promise<boolean> => {
    try {
      // The backend requires the target round; rescheduling from this view stays within it.
      await rescheduleMatch(tournamentId, matchId, { targetRoundId: round.id, startTime });
    } catch (err) {
      handleGlobalError(err, 'Error', 'No se pudo reprogramar el partido');
      return false;
    }
    toast({ title: 'Partido reprogramado' });
    await refreshRound();
    return true;
  };

  // Matches from the backend carry the team with only its id (no players), but
  // availabilityProgress already exposes teamId -> real teamName: we use it to name the
  // teams. Without this they all fall back to the generic "Equipo 1/2" and the overlap warning (which compares
  // by name) yields false positives across unrelated matches.
  const teamNameById = useMemo(
    () => new Map((round.availabilityProgress ?? []).map((t) => [t.teamId, t.teamName])),
    [round.availabilityProgress],
  );
  const teamAvailabilityById = useMemo(
    () => new Map((round.availabilityProgress ?? []).map((t) => [t.teamId, t.slots ?? []])),
    [round.availabilityProgress],
  );
  // Derive the proposal from matchList.
  // `scheduled`/`pendingReschedule`/`conflict` come from the backend; the overlap flag
  // (`overlapWarning`) is computed client-side as a visual complement.
  const proposals = useMemo(() => markProposalConflicts(
    (round.matchList ?? []).map((m) => ({
      matchId: m.id,
      team1Name: teamNameById.get(m.team1?.id ?? '') ?? getTeamName(m.team1, 'Equipo 1'),
      team2Name: teamNameById.get(m.team2?.id ?? '') ?? getTeamName(m.team2, 'Equipo 2'),
      clubName: m.club?.name ?? null,
      date: m.startDate ? m.startDate.split('T')[0] : null,
      startTime: m.startDate ? m.startDate.split('T')[1]?.substring(0, 5) ?? null : null,
      recoverySuggestion: false,
      scheduled: m.scheduled ?? true,
      pendingReschedule: m.pendingReschedule ?? false,
      conflict: m.conflict ?? false,
      conflictMissingAvailability: m.conflictMissingAvailability ?? false,
      forcedReason: m.forcedReason ?? null,
      overlapWarning: false,
      team1Availability: teamAvailabilityById.get(m.team1?.id ?? '') ?? [],
      team2Availability: teamAvailabilityById.get(m.team2?.id ?? '') ?? [],
    })),
    matchDuration,
  ), [round.matchList, teamNameById, teamAvailabilityById, matchDuration]);

  const canOpen = round.status === 'CREATED';
  const canPropose = round.status === 'AVAILABILITY_OPEN';
  const canPublish = round.status === 'PROPOSED';
  const showProposal = ['PROPOSED', 'PUBLISHED', 'COMPLETED'].includes(round.status);
  const isReadOnly = ['PUBLISHED', 'COMPLETED'].includes(round.status);
  // Schedule editor available until availability submission opens (inclusive)
  const canEditSchedule = ['CREATED', 'AVAILABILITY_OPEN'].includes(round.status);

  // Rounds of this same tournament can share the week too; they are not "other tournaments".
  const siblingRounds = (round.siblingRounds ?? []).filter((s) => s.tournamentId !== tournamentId);
  const openSiblings = siblingRounds.filter((s) => s.status === 'AVAILABILITY_OPEN');
  // The week's slots are shared: once any of its rounds is proposed, they can't move.
  const lockedSibling = siblingRounds.find((s) =>
    ['PROPOSED', 'PUBLISHED', 'COMPLETED'].includes(s.status),
  );
  const proposeDescription =
    openSiblings.length === 0
      ? 'Se van a ubicar los partidos de esta fecha en los horarios de la semana.'
      : `La propuesta reparte las canchas de la semana entre los torneos que la comparten, así que también se proponen: ${openSiblings
          .map(
            (s) =>
              `${s.tournamentName} (fecha ${s.roundNumber}, ${s.teamsWithAvailability} de ${s.totalTeams} equipos con disponibilidad)`,
          )
          .join('; ')}.`;

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center gap-3">
        <Button variant="ghost" size="sm" onClick={onBack}>
          <ChevronLeft className="mr-1 h-4 w-4" />
          Volver
        </Button>
        <div>
          <h3 className="text-lg font-semibold">Fecha {round.roundNumber}</h3>
          <Badge variant="outline" className="text-xs">
            {getRoundStatusLabel(round.status)}
          </Badge>
        </div>
      </div>

      {siblingRounds.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Semana compartida</CardTitle>
          </CardHeader>
          <CardContent className="space-y-1 text-sm">
            <p className="text-muted-foreground">
              Estos torneos usan las mismas canchas esta semana:
            </p>
            <ul className="list-disc space-y-1 pl-5">
              {siblingRounds.map((s) => (
                <li key={s.roundId}>
                  <Link
                    to={`/torneos/${s.tournamentId}?jornada=${s.roundId}`}
                    className="font-medium text-blue-700 underline-offset-2 hover:underline"
                  >
                    {s.tournamentName}
                  </Link>{' '}
                  · fecha{' '}
                  {s.roundNumber} · {getRoundStatusLabel(s.status)} · {s.teamsWithAvailability}/
                  {s.totalTeams} equipos con disponibilidad
                </li>
              ))}
            </ul>
          </CardContent>
        </Card>
      )}

      {/* Acciones según estado */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Acciones</CardTitle>
        </CardHeader>
        <CardContent className="flex flex-wrap gap-2">
          {canOpen && (
            <Button onClick={handleOpen} disabled={isActioning}>
              {isActioning && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Abrir jornada
            </Button>
          )}
          {canPropose && (
            <Button onClick={() => refreshThen(() => setConfirmProposeOpen(true))} disabled={isActioning}>
              {isActioning && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Generar propuesta
            </Button>
          )}
          {canPublish && (
            <Button onClick={handlePublish} disabled={isActioning}>
              {isActioning && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Publicar jornada
            </Button>
          )}
          {!canOpen && !canPropose && !canPublish && (
            <p className="text-sm text-muted-foreground">
              {round.status === 'COMPLETED'
                ? 'Esta jornada está cerrada.'
                : 'No hay acciones disponibles para el estado actual.'}
            </p>
          )}
        </CardContent>
      </Card>

      {/* Round schedule editor */}
      {canEditSchedule && (
        <Card>
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle className="text-base">Horarios de la jornada</CardTitle>
            {!showScheduleEditor && club && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => refreshThen(() => setShowScheduleEditor(true))}
                disabled={isActioning || Boolean(lockedSibling)}
                title={
                  lockedSibling
                    ? `La fecha ${lockedSibling.roundNumber} de ${lockedSibling.tournamentName} ya está ${getRoundStatusLabel(lockedSibling.status).toLowerCase()}: los horarios de la semana no se pueden cambiar.`
                    : undefined
                }
              >
                <Pencil className="mr-1 h-4 w-4" />
                Editar horarios
              </Button>
            )}
          </CardHeader>
          <CardContent>
            {siblingRounds.length > 0 && (
              <p className="mb-3 rounded-md border border-amber-200 bg-amber-50 p-2 text-sm text-amber-800">
                Estos horarios son de toda la semana: el cambio también aplica a{' '}
                {siblingRounds.map((s) => s.tournamentName).join(', ')}.
              </p>
            )}
            {!club ? (
              <p className="text-sm text-muted-foreground">
                No hay un club asociado para editar los horarios de esta jornada.
              </p>
            ) : showScheduleEditor ? (
              <RoundScheduleEditor
                tournamentId={tournamentId}
                round={round}
                club={club}
                matchDuration={matchDuration}
                sportUid={sportUid}
                onClose={() => setShowScheduleEditor(false)}
                onSaved={(updated) => {
                  onRoundUpdated(updated);
                  setShowScheduleEditor(false);
                }}
              />
            ) : (
              <RoundScheduleSummary round={round} />
            )}
          </CardContent>
        </Card>
      )}

      {/* Team availability */}
      {['AVAILABILITY_OPEN', 'PROPOSED'].includes(round.status) &&
        round.availabilityProgress && (
        <AvailabilityProgressPanel progress={round.availabilityProgress} />
      )}

      {/* Match proposal */}
      {showProposal && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base">
              {isReadOnly ? 'Partidos' : 'Propuesta de partidos'}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <ProposalGrid
              proposals={proposals}
              readOnly={isReadOnly}
              onReschedule={handleReschedule}
              week={{ start: round.weekStartDate, end: round.weekEndDate ?? addDays(round.weekStartDate, 6) }}
            />
          </CardContent>
        </Card>
      )}

      <ConfirmationModal
        isOpen={confirmProposeOpen}
        onClose={() => setConfirmProposeOpen(false)}
        onConfirm={() => {
          setConfirmProposeOpen(false);
          handlePropose();
        }}
        title="Generar la propuesta de la semana"
        description={proposeDescription}
        confirmText="Generar propuesta"
        cancelText="Cancelar"
      />
    </div>
  );
}

// ============================================================================
// SCHEDULE SUMMARY (read-only while the editor is closed)
// ============================================================================

function RoundScheduleSummary({ round }: { round: TournamentRound }) {
  const schedule = round.schedule ?? [];
  if (schedule.length === 0) {
    return (
      <p className="text-sm text-muted-foreground">
        No hay horarios configurados para esta jornada todavía.
      </p>
    );
  }

  // Group by date
  const byDate = new Map<string, typeof schedule>();
  for (const slot of schedule) {
    const list = byDate.get(slot.date) ?? [];
    list.push(slot);
    byDate.set(slot.date, list);
  }

  return (
    <div className="space-y-3">
      {Array.from(byDate.entries())
        .sort(([a], [b]) => a.localeCompare(b))
        .map(([date, slots]) => (
          <div key={date} className="space-y-1">
            <p className="text-xs font-medium uppercase tracking-wide text-gray-600">
              {new Date(date + 'T00:00:00').toLocaleDateString('es-AR', {
                weekday: 'short',
                day: 'numeric',
                month: 'short',
              })}
            </p>
            <div className="flex flex-wrap gap-1.5">
              {slots
                .slice()
                .sort((a, b) => a.startTime.localeCompare(b.startTime))
                .map((slot, i) => (
                  <span
                    key={`${slot.startTime}-${i}`}
                    className="flex items-center gap-1.5 rounded-md bg-gray-100 px-2 py-1 text-xs text-gray-700"
                  >
                    {slot.startTime}–{slot.endTime}
                    <Badge variant="secondary" className="h-4 px-1 text-[10px]">
                      {slot.courtsQuantity}
                    </Badge>
                  </span>
                ))}
            </div>
          </div>
        ))}
    </div>
  );
}
